import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { getPublicSite, submitPublicRequest, uploadPublicAttachment } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle2, MapPin, Wrench, Paperclip } from 'lucide-react';
import LutromLogo from '../components/LutromLogo';
import PoweredByLutrom from '../components/PoweredByLutrom';
import OutOfHoursBanner from '../components/OutOfHoursBanner';
import { toast } from 'sonner';

const PublicQrRequest = () => {
  const { siteId } = useParams();
  const [searchParams] = useSearchParams();
  const assetId = searchParams.get('asset');
  const [siteData, setSiteData] = useState(null);
  const [error, setError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', title: '', description: '', priority: 'medium' });

  useEffect(() => {
    getPublicSite(siteId, assetId)
      .then(setSiteData)
      .catch(() => setError(true));
  }, [siteId, assetId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.name || !form.email) return toast.error('Please fill required fields');
    setSubmitting(true);
    try {
      const res = await submitPublicRequest({ siteId, assetId, ...form });
      if (pendingFile) {
        try { await uploadPublicAttachment(res.id, pendingFile); }
        catch { /* attachment failure does not invalidate request */ }
      }
      setConfirmation(res);
      setSubmitted(true);
    } catch {
      toast.error('Submission failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
      <OutOfHoursBanner />
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8 text-center mt-6">
        <h1 className="font-display uppercase text-xl text-slate-900 mb-2">Invalid QR Code</h1>
        <p className="text-sm text-slate-600 mb-4">This QR code could not be matched to a Lutrom site.</p>
        <Link to="/"><Button>Go Home</Button></Link>
      </div>
      <PoweredByLutrom />
    </div>
  );

  if (!siteData) return (
    <div className="min-h-screen flex flex-col">
      <OutOfHoursBanner />
      <div className="flex-1 flex items-center justify-center text-slate-500">Loading...</div>
    </div>
  );

  const { site, asset } = siteData;

  return (
    <div className="min-h-screen bg-slate-50">
      <OutOfHoursBanner />
      <header className="bg-slate-900 text-white">
        <div className="max-w-2xl mx-auto p-6 flex items-center justify-between">
          <Link to="/"><LutromLogo dark size="sm" /></Link>
          <span className="text-xs font-mono text-blue-400">{siteId}</span>
        </div>
      </header>
      <main className="max-w-2xl mx-auto p-6">
        {submitted ? (
          <div className="bg-white border border-slate-200 rounded-md p-10 text-center">
            <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto mb-4" />
            <h1 className="font-display uppercase text-2xl text-slate-900 mb-2">Request Submitted</h1>
            <p className="text-slate-600 mb-2">Reference: <span className="font-mono font-semibold">{confirmation?.id}</span></p>
            <p className="text-slate-600 mb-6">Our team has been notified and will be in touch shortly.</p>
            <Link to="/"><Button variant="outline">Done</Button></Link>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <div className="mb-6 pb-6 border-b border-slate-200">
              <div className="text-xs font-medium uppercase tracking-widest text-blue-700 mb-2">Quick Work Request</div>
              <h1 className="font-display uppercase text-2xl text-slate-900">Report an Issue</h1>
              <div className="flex items-center gap-4 mt-3 text-sm text-slate-600 flex-wrap">
                <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {site.name}</span>
                {asset && <span className="flex items-center gap-1"><Wrench className="h-4 w-4" /> {asset.name}</span>}
              </div>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Your Name *</Label><Input className="mt-1.5" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Email *</Label><Input className="mt-1.5" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              </div>
              <div><Label>Phone</Label><Input className="mt-1.5" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+44..." /></div>
              <div><Label>Issue Title *</Label><Input className="mt-1.5" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Strange noise from motor" /></div>
              <div><Label>Description</Label><Textarea className="mt-1.5" rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What's happening? When did it start?" /></div>
              <div>
                <Label>Photo (optional)</Label>
                <div className="mt-1.5 flex items-center gap-2">
                  <input id="qr-file" type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={e => setPendingFile(e.target.files?.[0] || null)} />
                  <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('qr-file')?.click()}>
                    <Paperclip className="h-4 w-4 mr-2" /> {pendingFile ? 'Change file' : 'Take a photo / attach'}
                  </Button>
                  {pendingFile && <span className="text-xs text-slate-600 truncate">{pendingFile.name}</span>}
                </div>
              </div>
              <div><Label>Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low - When convenient</SelectItem>
                    <SelectItem value="medium">Medium - This week</SelectItem>
                    <SelectItem value="high">High - Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={submitting} className="w-full h-11 bg-slate-900 hover:bg-slate-800">{submitting ? 'Submitting...' : 'Submit Request'}</Button>
            </form>
          </div>
        )}
        <PoweredByLutrom />
      </main>
    </div>
  );
};

export default PublicQrRequest;
